import React from 'react';
import { Card, Badge, Button, Row, Col } from 'react-bootstrap';
import { MapPin, Bed, Bath, Square, Calendar, Heart, ExternalLink } from 'lucide-react';

export interface Property {
  id: string;
  mlsNumber: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  price: number;
  beds: number;
  baths: number;
  sqft: number;
  propertyType: string;
  yearBuilt: number;
  listingDate: string;
  status: 'active' | 'pending' | 'sold';
  description: string;
  images: string[];
  features: string[];
  agent?: {
    name: string;
    phone: string;
    email: string;
  };
}

interface PropertyCardProps {
  property: Property;
  onViewDetails?: (property: Property) => void;
  onFavorite?: (propertyId: string) => void;
  isFavorite?: boolean;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ 
  property, 
  onViewDetails, 
  onFavorite,
  isFavorite = false 
}) => {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { bg: 'success', text: 'Active' },
      pending: { bg: 'warning', text: 'Pending' },
      sold: { bg: 'danger', text: 'Sold' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
    
    return (
      <Badge bg={config.bg} className="position-absolute top-0 start-0 m-3">
        {config.text}
      </Badge>
    );
  };

  const primaryImage = property.images?.[0] || '/assets/img/property-placeholder.jpg';

  return (
    <Card className="property-card h-100 shadow-sm hover-shadow transition">
      <div className="position-relative">
        <Card.Img 
          variant="top" 
          src={primaryImage} 
          alt={property.address}
          style={{ height: '250px', objectFit: 'cover' }}
        />
        {getStatusBadge(property.status)}
        <Button
          variant="light"
          size="sm"
          className="position-absolute top-0 end-0 m-3 rounded-circle p-2"
          onClick={() => onFavorite?.(property.id)}
        >
          <Heart size={18} fill={isFavorite ? '#e74c3c' : 'none'} color="#e74c3c" />
        </Button>
      </div>
      
      <Card.Body className="d-flex flex-column">
        <div className="mb-3">
          <h3 className="h4 mb-1">{formatPrice(property.price)}</h3>
          <p className="text-muted mb-2">
            <MapPin size={16} className="me-1" />
            {property.address}
          </p>
          <p className="text-muted small">
            {property.city}, {property.state} {property.zipCode}
          </p>
        </div>

        <Row className="mb-3 g-3">
          <Col xs={6} sm={3}>
            <div className="d-flex align-items-center">
              <Bed size={16} className="me-1 text-primary" />
              <span className="small">{property.beds} Beds</span>
            </div>
          </Col>
          <Col xs={6} sm={3}>
            <div className="d-flex align-items-center">
              <Bath size={16} className="me-1 text-primary" />
              <span className="small">{property.baths} Baths</span>
            </div>
          </Col>
          <Col xs={6} sm={3}>
            <div className="d-flex align-items-center">
              <Square size={16} className="me-1 text-primary" />
              <span className="small">{property.sqft.toLocaleString()} sqft</span>
            </div>
          </Col>
          <Col xs={6} sm={3}>
            <div className="d-flex align-items-center">
              <Calendar size={16} className="me-1 text-primary" />
              <span className="small">{property.yearBuilt}</span>
            </div>
          </Col>
        </Row>

        <p className="text-muted small mb-3 flex-grow-1">
          {property.description.length > 100 
            ? `${property.description.substring(0, 100)}...` 
            : property.description}
        </p>

        <div className="d-flex gap-2 mt-auto">
          <Button 
            variant="primary" 
            onClick={() => onViewDetails?.(property)}
            className="flex-fill"
          >
            View Details
          </Button>
          <Button variant="outline-primary">
            <ExternalLink size={16} />
          </Button>
        </div>

        <div className="mt-2 text-muted small">
          MLS# {property.mlsNumber}
        </div>
      </Card.Body>
    </Card>
  );
};

export default PropertyCard;